// Real-time Messaging Hook
//
// Live typing indicators and new-message nudges for one conversation, over the
// Supabase Realtime broadcast channel messagingService owns.
//
// THIS USED TO INVENT BOTH. A timer rolled a 20% chance every 10 seconds and,
// when it hit, added a typing indicator from a hardcoded user called "Other
// User" for 3 seconds. Nobody was typing — the app was telling people their
// match was writing to them roughly every fifty seconds, at random, including
// when the other person was not even online.
//
// Everything below is now driven by events another client actually sent.

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Message,
  TypingUser,
  UseRealtimeMessagingReturn,
} from '../../types/messaging';
import { messagingService } from '../../services/Messaging/messagingService';

/**
 * How long a typing indicator survives without a refresh.
 *
 * A "stopped typing" event can always be lost — a closed tab, a dropped
 * connection — so the indicator has to expire on its own. Without this, one
 * missed event leaves "typing…" on screen forever, which is the same lie the
 * mock told, just less often.
 */
const TYPING_TIMEOUT_MS = 5000;

interface RealtimeOptions {
  /** Resolves a user id to a display name for the indicator. */
  participants?: Array<{ id: string; name: string }>;
  /** Fired when someone else sends a message; refetch through the API. */
  onNewMessage?: () => void;
}

export const useRealtimeMessaging = (
  conversationId: string,
  options: RealtimeOptions = {},
): UseRealtimeMessagingReturn => {
  const { participants, onNewMessage } = options;
  const [messages] = useState<Message[]>([]);
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);

  // Held in refs so the subscription effect depends on the conversation alone.
  // Re-subscribing every time the parent re-renders would tear the channel
  // down and rebuild it mid-conversation.
  const onNewMessageRef = useRef(onNewMessage);
  const participantsRef = useRef(participants);
  useEffect(() => { onNewMessageRef.current = onNewMessage; }, [onNewMessage]);
  useEffect(() => { participantsRef.current = participants; }, [participants]);

  const currentUserIdRef = useRef<string | null>(null);
  const expiryTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const clearTypingFor = useCallback((userId: string) => {
    setTypingUsers((prev) => prev.filter((u) => u.userId !== userId));
    const timer = expiryTimers.current.get(userId);
    if (timer) {
      clearTimeout(timer);
      expiryTimers.current.delete(userId);
    }
  }, []);

  useEffect(() => {
    if (!conversationId) return;

    const timers = expiryTimers.current;

    const unsubscribe = messagingService.subscribeToConversation(
      conversationId,
      () => onNewMessageRef.current?.(),
      (userId, isTyping) => {
        // Belt and braces: the channel is configured with self: false, so our
        // own events should never arrive, but showing yourself typing is such
        // a specific embarrassment that it is worth checking twice.
        if (userId === currentUserIdRef.current) return;

        if (!isTyping) {
          clearTypingFor(userId);
          return;
        }

        const name = participantsRef.current?.find((p) => p.id === userId)?.name;
        setTypingUsers((prev) => {
          const existing = prev.find((u) => u.userId === userId);
          if (existing) {
            return prev.map((u) =>
              u.userId === userId ? { ...u, isTyping: true, lastTypingAt: new Date() } : u,
            );
          }
          return [...prev, {
            userId,
            // Falls back to something neutral rather than a fabricated name.
            userName: name || 'Someone',
            isTyping: true,
            lastTypingAt: new Date(),
          }];
        });

        const previousTimer = timers.get(userId);
        if (previousTimer) clearTimeout(previousTimer);
        timers.set(userId, setTimeout(() => clearTypingFor(userId), TYPING_TIMEOUT_MS));
      },
    );

    return () => {
      unsubscribe();
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
      setTypingUsers([]);
    };
  }, [conversationId, clearTypingFor]);

  const sendTypingIndicator = useCallback((isTyping: boolean, userId?: string) => {
    if (userId) currentUserIdRef.current = userId;
    messagingService.sendTypingIndicator(conversationId, isTyping, userId ?? currentUserIdRef.current ?? undefined);
  }, [conversationId]);

  return {
    messages,
    typingUsers,
    sendTypingIndicator,
  };
};
